"use client";

import { useEffect, useState } from "react";
import { ChatCircleDots, X } from "@phosphor-icons/react/dist/ssr";

import { Button } from "@/src/components/ui/Button";
import { SpinningTextRing } from "@/src/components/ui/SpinningTextRing";
import { cn } from "@/src/lib/utils";

import { CHAT_LAUNCHER_ACTIONS } from "./data";
import { ChatLauncherAction } from "./ChatLauncherAction";
import { useIsOverDarkSection } from "./useIsOverDarkSection";

const RING_TEXT = "TALK TO AN EXPERT • FREE CONSULT • ";
const BUTTON_SIZE = 64;
const RING_SIZE = 108;
const STAGGER_MS = 90;

export function ChatLauncher() {
  const [isOpen, setIsOpen] = useState(false);
  const isOverDarkSection = useIsOverDarkSection();

  // Matches the mobile-nav-drawer scroll-lock in Header.tsx — the menu
  // overlays the page instead of pushing it down, so background scroll must
  // be blocked while it's open. Clicks are blocked simply by the overlay
  // below physically covering the page and capturing the pointer itself.
  //
  // `overflow: hidden` alone removes the scrollbar, which widens the
  // viewport by its own width — the page content AND this `fixed` launcher
  // (anchored to the viewport's right edge) would jump right. Body padding
  // only fixes the content, not fixed elements, so instead reserve the
  // scrollbar's gutter on <html> while locked: the viewport width stays
  // identical and nothing moves.
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const previousOverflow = document.body.style.overflow;
    const previousGutter = root.style.scrollbarGutter;
    root.style.scrollbarGutter = "stable";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      root.style.scrollbarGutter = previousGutter;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  return (
    <>
      {/* No backdrop-blur — animating a blurred backdrop is expensive to
          repaint every frame, and doing so at the same time as the three
          action buttons animate in was the main source of the janky/
          laggy-feeling open transition. Mild flat tint only. */}
      <div
        aria-hidden
        onClick={() => setIsOpen(false)}
        className={cn(
          "fixed inset-0 z-60 cursor-pointer bg-white/40 transition-opacity duration-300",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      <div className="fixed bottom-5 right-5 z-70 flex flex-col items-end gap-4 sm:bottom-10 sm:right-10">
        {CHAT_LAUNCHER_ACTIONS.map((action, index) => (
          <ChatLauncherAction
            key={action.label}
            action={action}
            visible={isOpen}
            delayMs={(CHAT_LAUNCHER_ACTIONS.length - 1 - index) * STAGGER_MS}
          />
        ))}

        {/* Ring and button are siblings here, not parent/child — both
            centered the identical way (`absolute inset-0 m-auto`) inside
            this one explicitly-sized, `group`-tagged box, so they share the
            exact same center point by construction. The ring's own scale
            lives on a wrapping div rather than on SpinningTextRing's own
            root — that root already has the continuous spin animation on
            its `transform`, and a hover-triggered `transform: scale(...)`
            on the SAME element would fight the animation for that one CSS
            property, each replacing the other for as long as both are
            active (the same class of bug the .chip-float/.timeline-track
            comments in animations.css warn about). Splitting scale and
            rotate onto separate elements avoids that entirely. */}
        <div className="group relative" style={{ width: RING_SIZE, height: RING_SIZE }}>
          <div className="absolute inset-0 transition-transform duration-300 group-hover:scale-110">
            <SpinningTextRing text={RING_TEXT} size={RING_SIZE} isDark={isOverDarkSection} />
          </div>

          <Button
            type="button"
            unstyled
            aria-expanded={isOpen}
            aria-label={isOpen ? "Close contact options" : "Open contact options"}
            onClick={() => setIsOpen((prev) => !prev)}
            className="absolute inset-0 m-auto flex items-center justify-center rounded-full bg-neutral-700 shadow-[0_0_20px_rgba(32,34,35,0.35)] transition-transform duration-300 group-hover:scale-105"
            style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }}
          >
            {isOpen ? (
              <X size={24} weight="bold" className="text-white" />
            ) : (
              <ChatCircleDots size={26} weight="bold" className="text-white" />
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
